import * as React from 'react';

import {
  enumUnit,
  PurchaseLineItemType,
  Unit,
} from '@rematter/frontend-lib-api-client';
import { zeroWeight } from '@rematter/modules/trade/utils/weight';
import { TouchButton } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideTag } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

import {
  InventoryItemsModal,
  InventoryTagData,
} from '../../../_shared/InventoryItemsModal';
import {
  $itemsInterfaceHighlightedMaterialIds,
  $itemsInterfaceHighlightedSectionTitle,
  $itemsInterfaceSuggestedPrices,
  resolveSuggestedPrice,
} from '../_state';
import { NormalizedLineItem } from '../_types';

import { $inventoryItemsById } from '~/modules/trade/_state/loadForm.state';
import { $setAutoFocusRowId } from '~/modules/trade/_state/materialsInterface.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { enumLoadWeightType } from '~/modules/trade/types/load';
import { useUIPreferences } from '~/shared/hooks';

const ICON_TAG = <LucideTag />;

type Props = {
  onAddItem?: (
    type: PurchaseLineItemType,
    data?: Partial<NormalizedLineItem>,
  ) => void;
  modalStyle?: React.CSSProperties;
  buttonSize?: 'sm' | 'md' | 'lg' | 'xl';
};

// ⚛️ ⚛️ ⚛️ ADD TAGGED MATERIAL WIDGET ⚛️ ⚛️ ⚛️
export const AddTaggedMaterialWidget: React.FC<Props> = ({
  onAddItem,
  modalStyle,
  buttonSize,
}) => {
  // Translations
  const t = useTranslations();

  // States
  const [inventoryModalOpen, setInventoryModalOpen] = React.useState(false);

  // Atoms
  const setAutoFocusRowId = useSetAtom($setAutoFocusRowId);
  const highlightedMaterialIds = useAtomValue(
    $itemsInterfaceHighlightedMaterialIds,
  );
  const highlightedSectionTitle = useAtomValue(
    $itemsInterfaceHighlightedSectionTitle,
  );
  const suggestedPrices = useAtomValue($itemsInterfaceSuggestedPrices);
  const inventoryItemsById = useAtomValue($inventoryItemsById);

  // Data
  const { touchModeEnabled } = useUIPreferences();

  // Callbacks
  const handleAddEmptyTaggedRow = React.useCallback(() => {
    if (!onAddItem) return;
    const newItemId = `temp-${uuidv4()}`;
    onAddItem('Tagged', {
      id: newItemId,
      itemId: null,
      tagId: null,
      weights: {
        [enumLoadWeightType.Gross]: zeroWeight(enumUnit.Pounds),
        [enumLoadWeightType.Tare]: zeroWeight(enumUnit.Pounds),
        [enumLoadWeightType.Net]: zeroWeight(enumUnit.Pounds),
      },
      unitPrice: null,
      pricingUnit: enumUnit.Pounds,
    });
    setAutoFocusRowId(newItemId);
  }, [onAddItem, setAutoFocusRowId]);

  const handleAddTaggedFromItem = React.useCallback(
    (materialId?: string) => {
      if (!onAddItem) return;
      const newItemId = `temp-${uuidv4()}`;
      const suggested = resolveSuggestedPrice(suggestedPrices, materialId);
      const item = materialId ? inventoryItemsById[materialId] : undefined;
      const unit = (item?.effectivePricingUnit as Unit) ?? enumUnit.Pounds;
      onAddItem('Tagged', {
        id: newItemId,
        itemId: materialId ?? null,
        tagId: null,
        weights: {
          [enumLoadWeightType.Gross]: zeroWeight(unit),
          [enumLoadWeightType.Tare]: zeroWeight(unit),
          [enumLoadWeightType.Net]: zeroWeight(unit),
        },
        unitPrice: suggested?.unitPrice ?? null,
        pricingUnit: unit,
      });
      setAutoFocusRowId(newItemId);
      setInventoryModalOpen(false);
    },
    [onAddItem, setAutoFocusRowId, suggestedPrices, inventoryItemsById],
  );

  const handleSelectInventoryTag = React.useCallback(
    (_tagId: string, tagData: InventoryTagData) => {
      if (!onAddItem) return;
      const newItemId = `temp-${uuidv4()}`;
      const materialId = tagData.item.id;
      const suggested = resolveSuggestedPrice(suggestedPrices, materialId);
      const unit =
        (tagData.item.effectivePricingUnit as Unit) ?? enumUnit.Pounds;
      onAddItem('Tagged', {
        id: newItemId,
        itemId: materialId,
        tagId: tagData.id,
        weights: {
          [enumLoadWeightType.Gross]: {
            ...zeroWeight(unit),
            value: tagData.grossQuantity,
          },
          [enumLoadWeightType.Tare]: {
            ...zeroWeight(unit),
            value: tagData.tare,
          },
          [enumLoadWeightType.Net]: {
            ...zeroWeight(unit),
            value: tagData.netQuantity,
          },
        },
        unitPrice: suggested?.unitPrice ?? null,
        pricingUnit: unit,
      });
      setAutoFocusRowId(newItemId);
      setInventoryModalOpen(false);
    },
    [onAddItem, setAutoFocusRowId, suggestedPrices],
  );

  const handleModalOpenChange = React.useCallback((isOpen: boolean) => {
    if (!isOpen) setInventoryModalOpen(false);
  }, []);

  const handlePress = React.useCallback(() => {
    if (touchModeEnabled) {
      setInventoryModalOpen(true);
    } else {
      handleAddEmptyTaggedRow();
    }
  }, [touchModeEnabled, handleAddEmptyTaggedRow]);

  return (
    <>
      <TouchButton
        onPress={handlePress}
        className="@AddTaggedMaterialWidget w-full"
        isSelected={false}
        leftElement={ICON_TAG}
        color="primary"
        variant="outline"
        size={buttonSize}
        aria-label="Add Item"
      >
        {t.scaleTicketPro.actions.addTagged}
      </TouchButton>

      {touchModeEnabled && (
        <InventoryItemsModal
          isOpen={inventoryModalOpen}
          mode="tags"
          onOpenChange={handleModalOpenChange}
          onSelectMaterial={handleAddTaggedFromItem}
          onSelectInventoryTag={handleSelectInventoryTag}
          modalStyle={modalStyle}
          highlightedMaterialIds={highlightedMaterialIds}
          highlightedSectionTitle={highlightedSectionTitle}
        />
      )}
    </>
  );
};
